import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Button, Field, Badge, Icon } from "@/components/ui";
import { useAuth, roleHome } from "@/context/AuthContext";
import { api, ApiError, setSession } from "@/lib/api";
import { AuthBackdrop } from "@/components/immersive/ImmersiveShell";

export default function Login() {
  const nav = useNavigate();
  const { login, verifyMfa, resetPassword, setUser } = useAuth();
  const [phase, setPhase] = useState("credentials");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [challenge, setChallenge] = useState(null);
  const [code, setCode] = useState("");
  const [resetToken, setResetToken] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [needsVerify, setNeedsVerify] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);
  const googleRan = useRef(false);

  // Handle the return from Emergent Google Auth: URL ends with #session_id=...
  useEffect(() => {
    const hash = window.location.hash || "";
    if (!hash.includes("session_id=")) return;
    if (googleRan.current) return; // session_id is single-use — guard StrictMode double-invoke
    googleRan.current = true;
    const sid = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
    window.history.replaceState(null, "", window.location.pathname);
    if (!sid) return;
    setGoogleBusy(true); setError(null);
    (async () => {
      try {
        const res = await api.post("/auth/google/session", { session_id: sid }, false);
        setSession(res.access_token, res.refresh_token);
        setUser(res.user);
        nav(roleHome(res.user.role), { replace: true });
      } catch (err) {
        setError(err instanceof ApiError ? err.message : "Google sign-in failed. Please try again.");
      } finally { setGoogleBusy(false); }
    })();
  }, [nav, setUser]);

  const startGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/login";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };


  const startLogin = async () => {
    setBusy(true); setError(null); setNotice(null); setNeedsVerify(false);
    try {
      const res = await login(email.trim(), password);
      if (res.access_token) { setSession(res.access_token, res.refresh_token); setUser(res.user); nav(roleHome(res.user.role), { replace: true }); return; }
      if (res.status === "password_reset_required") { setResetToken(res.reset_token); setPhase("reset"); }
      else { setChallenge({ id: res.challenge_id, masked: res.masked_email }); setPhase("mfa"); }
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "Login failed";
      if (/verify your email/i.test(msg)) setNeedsVerify(true);
      setError(msg);
    }
    finally { setBusy(false); }
  };

  const resendVerification = async () => {
    setBusy(true); setError(null);
    try {
      await api.post("/auth/resend-verification", { email: email.trim(), origin_url: window.location.origin }, false);
      setNotice("Verification email sent. Check your inbox, then sign in.");
      setNeedsVerify(false);
    } catch (err) { setError(err instanceof ApiError ? err.message : "Could not resend"); }
    finally { setBusy(false); }
  };

  const finishMfa = async () => {
    if (!challenge?.id) return;
    setBusy(true); setError(null);
    try { const user = await verifyMfa(challenge.id, code.trim()); nav(roleHome(user.role), { replace: true }); }
    catch (err) { setError(err instanceof ApiError ? err.message : "Verification failed"); }
    finally { setBusy(false); }
  };

  const finishReset = async () => {
    if (!resetToken) return;
    if (newPw.length < 12) { setError("Use at least 12 characters."); return; }
    if (newPw !== confirmPw) { setError("Passwords do not match."); return; }
    setBusy(true); setError(null);
    try {
      await resetPassword(resetToken, newPw);
      setPhase("credentials"); setResetToken(null); setPassword(""); setNewPw(""); setConfirmPw("");
      setNotice("Password updated. Sign in with your new password.");
    } catch (err) { setError(err instanceof ApiError ? err.message : "Could not set password"); }
    finally { setBusy(false); }
  };

  const sendForgot = async () => {
    if (!email.includes("@")) { setError("Enter the email on your account."); return; }
    setBusy(true); setError(null);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() }, false);
      setPhase("forgot_reset"); setNotice(null); setNewPw(""); setConfirmPw(""); setCode("");
    } catch (err) { setError(err instanceof ApiError ? err.message : "Could not send reset code"); }
    finally { setBusy(false); }
  };

  const finishForgot = async () => {
    if (code.trim().length < 6) { setError("Enter the 6-digit code from your email."); return; }
    if (newPw.length < 8) { setError("Use at least 8 characters."); return; }
    if (newPw !== confirmPw) { setError("Passwords do not match."); return; }
    setBusy(true); setError(null);
    try {
      await api.post("/auth/reset-password-code", { email: email.trim(), code: code.trim(), new_password: newPw }, false);
      setPhase("credentials"); setPassword(""); setNewPw(""); setConfirmPw(""); setCode("");
      setNotice("Password updated. Sign in with your new password.");
    } catch (err) { setError(err instanceof ApiError ? err.message : "Could not reset password"); }
    finally { setBusy(false); }
  };

  const title = phase === "mfa" ? "Verify it's you" : phase === "reset" ? "Set a new password"
    : phase === "forgot" ? "Reset your password" : phase === "forgot_reset" ? "Enter code & new password" : "Welcome back";

  return (
    <AuthBackdrop>
      <div data-testid="login-screen">
        <div className="imm-auth-logo">
          <img src="/homecarematches-logo.png" alt="HomecareMatches" onClick={() => nav("/")} />
        </div>
        <h1 className="imm-auth-title">{title}</h1>
        <p className="imm-auth-sub">Connecting care · Improving lives</p>

        {phase === "credentials" && (
          <>
            {notice ? <div style={{ marginBottom: 10, textAlign: "center" }}><Badge testID="login-notice" label={notice} tone="success" /></div> : null}
            <Card className="imm-auth-card">
              <Field testID="login-email" label="Email" value={email} onChange={setEmail} placeholder="you@example.com" />
              <Field testID="login-password" label="Password" type="password" value={password} onChange={setPassword} placeholder="••••••••" />
              {error ? <div className="err-text" data-testid="login-error">{error}</div> : null}
              {needsVerify ? <div style={{ marginTop: 8 }}><Button testID="login-resend-verify" variant="secondary" icon="mail" label="Resend verification email" onClick={resendVerification} loading={busy} /></div> : null}
              <Button testID="login-submit" label="Sign in" onClick={startLogin} loading={busy} />
              <button className="linkish" data-testid="login-forgot" onClick={() => { setPhase("forgot"); setError(null); setNotice(null); }} style={{ background: "none", border: "none", color: "var(--imm-sky)", cursor: "pointer", fontSize: 13, textDecoration: "underline", padding: "10px 0 0", display: "block", margin: "0 auto" }}>Forgot your password?</button>
              <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "14px 0 10px", opacity: 0.7 }}>
                <div style={{ flex: 1, height: 1, background: "var(--border-strong, rgba(255,255,255,0.25))" }} />
                <span className="note" style={{ fontSize: 12 }}>or</span>
                <div style={{ flex: 1, height: 1, background: "var(--border-strong, rgba(255,255,255,0.25))" }} />
              </div>
              <Button testID="login-google" variant="secondary" icon="log-in" label="Continue with Google" onClick={startGoogle} loading={googleBusy} />
              <p className="note" style={{ textAlign: "center", marginTop: 8, fontSize: 12 }}>Google sign-in is available for families & caregivers with an existing account.</p>
            </Card>
            <p className="note" style={{ textAlign: "center" }}>Clients sign in with email + password. Staff & agencies get a 6-digit code by email · RBAC enforced server-side.</p>
          </>
        )}

        {phase === "mfa" && challenge && (
          <Card className="imm-auth-card">
            <div className="path-icon" style={{ marginBottom: 12 }}><Icon name="shield" size={22} /></div>
            <h2 className="h2">Enter your verification code</h2>
            <p className="summary">We emailed a 6-digit code to {challenge.masked}. It expires in 10 minutes.</p>
            <Field testID="mfa-code" label="6-digit code" value={code} onChange={setCode} placeholder="123456" inputMode="numeric" />
            {error ? <div className="err-text" data-testid="mfa-error">{error}</div> : null}
            <Button testID="mfa-submit" label="Verify & continue" onClick={finishMfa} loading={busy} />
            <div style={{ height: 8 }} />
            <Button variant="ghost" label="Back" onClick={() => { setPhase("credentials"); setError(null); setCode(""); }} />
          </Card>
        )}

        {phase === "forgot" && (
          <Card className="imm-auth-card">
            <div className="path-icon" style={{ marginBottom: 12 }}><Icon name="key" size={22} /></div>
            <h2 className="h2">Reset your password</h2>
            <p className="summary">Enter your account email and we'll send a 6-digit reset code. If an account exists, you'll receive it shortly.</p>
            <Field testID="forgot-email" label="Account email" value={email} onChange={setEmail} placeholder="you@example.com" inputMode="email" />
            {error ? <div className="err-text" data-testid="forgot-error">{error}</div> : null}
            <Button testID="forgot-send" label="Send reset code" onClick={sendForgot} loading={busy} />
            <div style={{ height: 8 }} />
            <Button variant="ghost" label="Back to sign in" onClick={() => { setPhase("credentials"); setError(null); }} />
          </Card>
        )}

        {phase === "forgot_reset" && (
          <Card className="imm-auth-card">
            <div className="path-icon" style={{ marginBottom: 12 }}><Icon name="lock" size={22} /></div>
            <h2 className="h2">Enter code & new password</h2>
            <p className="summary">We sent a 6-digit code to <b>{email}</b> (check spam too). Enter it with your new password. Code expires in 30 minutes.</p>
            <Field testID="forgot-code" label="6-digit code" value={code} onChange={setCode} placeholder="123456" inputMode="numeric" maxLength={6} />
            <Field testID="forgot-new" label="New password" type="password" value={newPw} onChange={setNewPw} placeholder="At least 8 characters" />
            <Field testID="forgot-confirm" label="Confirm new password" type="password" value={confirmPw} onChange={setConfirmPw} placeholder="Re-enter password" />
            {error ? <div className="err-text" data-testid="forgot-reset-error">{error}</div> : null}
            <Button testID="forgot-reset-submit" label="Reset password & sign in" onClick={finishForgot} loading={busy} />
            <div style={{ height: 8 }} />
            <Button variant="ghost" label="Resend code" onClick={sendForgot} loading={busy} />
          </Card>
        )}

        {phase === "reset" && (
          <Card className="imm-auth-card">
            <div className="path-icon" style={{ marginBottom: 12 }}><Icon name="key" size={22} /></div>
            <h2 className="h2">Create your password</h2>
            <p className="summary">This is your first sign-in. Choose a new password (at least 12 characters).</p>
            <Field testID="reset-password" label="New password" type="password" value={newPw} onChange={setNewPw} placeholder="••••••••••••" />
            <Field testID="reset-confirm" label="Confirm password" type="password" value={confirmPw} onChange={setConfirmPw} placeholder="••••••••••••" />
            {error ? <div className="err-text" data-testid="reset-error">{error}</div> : null}
            <Button testID="reset-submit" label="Set password & continue" onClick={finishReset} loading={busy} />
          </Card>
        )}

        <div className="footer-links" style={{ justifyContent: "center", marginTop: 20 }}>
          <a data-testid="login-terms" onClick={() => nav("/terms")}>Terms</a>
          <a data-testid="login-privacy" onClick={() => nav("/privacy")}>Privacy Policy</a>
          <a data-testid="login-home" onClick={() => nav("/")}>Home</a>
        </div>
      </div>
    </AuthBackdrop>
  );
}
